var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useImmer } from 'use-immer';
import { FaPlus, FaTrash, FaTimes, FaRandom, FaPencilAlt, FaSearch, FaFileImport, FaDownload, FaLink, FaMap, FaImage } from 'react-icons/fa';
import { ABILITY_COLORS, PrizeType, createDefaultEvent } from '../../api/models';
import { ABILITY_ICON } from '../../utils/gameIcons';
import { IconToken } from '../common/IconToken';
import { ImageSelectModal } from '../ImageSelectModal/ImageSelectModal';
import './EventEditor.scss';
import { createEvent, deleteEvent, getArchivedEvents, getLevelEvents, restoreEvent, updateEvent } from 'src/api/level-events';
import { AvatarAbility, backendUrl, DEFAULT_TRIAL_DIFFICULTY } from '@common/contracts';
import { getEventImages } from 'src/api/event-images';
import { EventBus } from '../../utils/EventBus';
import { getLevelScenarios, updateScenario } from '../../api/scenarios';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { getLevelById } from '../../api/levels';
// Normalizes the image URL, prepending backendUrl if it is not a full URL
const normalizeImageUrl = (url) => {
    if (!url)
        return undefined;
    return url.startsWith('http') ? url : `${backendUrl}${url}`;
};
// Maximum number of trial options (abilities)
const MAX_TRIAL_OPTIONS = 2;
// Semi-transparent colors for thin ability borders
const ABILITY_BORDER_COLORS = {
    [AvatarAbility.Strength]: 'rgba(244, 67, 54, 0.4)',
    [AvatarAbility.Agility]: 'rgba(33, 150, 243, 0.4)',
    [AvatarAbility.Perception]: 'rgba(156, 39, 176, 0.4)',
    [AvatarAbility.Intellect]: 'rgba(76, 175, 80, 0.4)',
    [AvatarAbility.Defence]: 'rgba(255, 193, 7, 0.4)'
};
export const EventEditor = ({ levelId, readOnly = false, onEventsMutated, onUnsavedChangesChange, onRegisterSave }) => {
    // List of all events for the current level
    const [events, setEvents] = useImmer([]);
    // The event currently being edited
    const [currentEvent, setCurrentEvent] = useImmer(null);
    // State for managing the interface
    const [isEditing, setIsEditing] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    // State for search and filtering
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
    // New filters
    const [connectionFilter, setConnectionFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [filteredEvents, setFilteredEvents] = useImmer([]);
    // Soft-deleted events, shown in the restorable Archive section below the grid.
    const [archivedEvents, setArchivedEvents] = useImmer([]);
    const [restoringId, setRestoringId] = useState(null);
    // Level scenarios (for displaying links)
    const [scenarios, setScenarios] = useImmer([]);
    // Ref for file uploads
    const fileInputRef = useRef(null);
    // State for event images and selection modals
    const [eventImages, setEventImages] = useImmer([]);
    const [showBackgroundModal, setShowBackgroundModal] = useState(false);
    const [showCardModal, setShowCardModal] = useState(false);
    // Load event images
    useEffect(() => {
        const fetchEventImages = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const images = yield getEventImages(levelId);
                setEventImages(images.filter(img => !img.isArchived));
            }
            catch (error) {
                console.error('Error loading event images:', error);
            }
        });
        fetchEventImages();
    }, [levelId, setEventImages]);
    // Function to check whether an event image is used (similar to DialogueEditor)
    const isEventImageUsed = useCallback((imageUrl) => {
        // Check usage as an event background image
        const usedAsBackground = events.some(event => {
            return normalizeImageUrl(event.backgroundImage) === normalizeImageUrl(imageUrl);
        });
        if (usedAsBackground)
            return true;
        // Check usage as an event card image
        const usedAsCard = events.some(event => {
            return normalizeImageUrl(event.cardImage) === normalizeImageUrl(imageUrl);
        });
        return usedAsCard;
    }, [events]);
    // Level nodes (for finding links via Random Encounters)
    const [levelNodes, setLevelNodes] = useImmer({});
    // State for the delete confirmation modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteWarningMessage, setDeleteWarningMessage] = useState('');
    const [pendingDeleteEventId, setPendingDeleteEventId] = useState(null);
    // State for the save confirmation modal
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [saveWarningMessage, setSaveWarningMessage] = useState('');
    const [pendingEventToSave, setPendingEventToSave] = useState(null);
    // --- Unsaved changes tracking ---
    const originalEventRef = useRef(null);
    // Save the original snapshot when editing begins
    useEffect(() => {
        if (isEditing && currentEvent) {
            originalEventRef.current = JSON.parse(JSON.stringify(currentEvent));
        }
        else {
            originalEventRef.current = null;
        }
    }, [isEditing]);
    const hasUnsavedChanges = useMemo(() => {
        if (!isEditing || !currentEvent || !originalEventRef.current)
            return false;
        return JSON.stringify(currentEvent) !== JSON.stringify(originalEventRef.current);
    }, [isEditing, currentEvent]);
    // Load events on initialization
    useEffect(() => {
        const fetchEvents = () => __awaiter(void 0, void 0, void 0, function* () {
            setIsLoading(true);
            setError(null);
            try {
                const eventsData = yield getLevelEvents(levelId);
                setEvents(eventsData);
            }
            catch (err) {
                console.error('Error fetching events:', err);
                setError(`Failed to load events: ${err.message}`);
            }
            finally {
                setIsLoading(false);
            }
        });
        fetchEvents();
        getArchivedEvents(levelId)
            .then(list => setArchivedEvents(list))
            .catch(err => console.error('Failed to load archived events', err));
        // Load the level scenarios
        getLevelScenarios(levelId)
            .then(data => setScenarios(data))
            .catch(err => console.error('Failed to load scenarios', err));
        // Load the level node data (for analyzing Random Encounters links)
        getLevelById(levelId)
            .then(level => {
            setLevelNodes(level.nodes || {});
        })
            .catch(err => console.error('Failed to load level nodes', err));
    }, [levelId, setEvents, setLevelNodes]);
    // Effect for filtering events
    useEffect(() => {
        // An event is "connected" if a map node attaches it via simpleEvents (the
        // path imported/quick-scenario events use) OR an interactive scenario node
        // references it. Checking only scenarios made every imported event read as
        // unconnected. Build the node-attachment set once.
        const nodeEventIds = new Set();
        Object.values(levelNodes).forEach(node => { var _a; return ((_a = node.simpleEvents) !== null && _a !== void 0 ? _a : []).forEach(se => { if (se.eventId)
            nodeEventIds.add(se.eventId); }); });
        setFilteredEvents(events.filter(event => {
            // Filter by title
            const titleMatches = searchTerm ?
                event.title.toLowerCase().includes(searchTerm.toLowerCase()) : true;
            // Filter by abilities
            const characteristicsMatch = selectedCharacteristics.length > 0 ?
                event.encounters.some(option => selectedCharacteristics.includes(option.ability)) : true;
            // Filter by links — map-node simpleEvents OR interactive-scenario nodes
            const hasConnections = nodeEventIds.has(event.id) || scenarios.some(sc => { var _a; return (_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId) === event.id; }); });
            const connectionMatches = connectionFilter === 'all' ||
                (connectionFilter === 'connected' && hasConnections) ||
                (connectionFilter === 'unconnected' && !hasConnections);
            // Filter by event type
            const typeMatches = typeFilter === 'all' ||
                (typeFilter === 'challenge' && !event.isEncounter) ||
                (typeFilter === 'encounter' && event.isEncounter);
            return titleMatches && characteristicsMatch && connectionMatches && typeMatches;
        }));
    }, [events, searchTerm, selectedCharacteristics, connectionFilter, typeFilter, scenarios, levelNodes, setFilteredEvents]);
    // Handler for creating a new event
    const handleCreateEvent = () => {
        const newEvent = createDefaultEvent(levelId);
        setCurrentEvent(newEvent);
        setIsEditing(true);
        setSelectedEventId(null);
        setError(null);
    };
    // Handler for editing an existing event
    const handleEditEvent = (eventId) => {
        const eventToEdit = events.find(e => e.id === eventId);
        if (eventToEdit) {
            setCurrentEvent(eventToEdit);
            setIsEditing(true);
            setSelectedEventId(eventId);
            setError(null);
        }
    };
    // Direct event deletion (called after confirmation)
    const handleDeleteEvent = (eventId_1, ...args_1) => __awaiter(void 0, [eventId_1, ...args_1], void 0, function* (eventId, skipBasicConfirm = false) {
        if (skipBasicConfirm || confirm('Are you sure you want to delete this event?')) {
            setIsLoading(true);
            setError(null);
            try {
                yield deleteEvent(levelId, eventId);
                yield syncScenarios(levelId, { eventId: eventId });
                const archived = events.find(e => e.id === eventId);
                setEvents(draft => draft.filter(e => e.id !== eventId));
                if (archived)
                    setArchivedEvents(draft => { draft.unshift(Object.assign(Object.assign({}, archived), { isArchived: true })); });
                // The server also detached the event from map cells — refresh our
                // node copy (attachment badges) and the parent editor's map state.
                getLevelById(levelId)
                    .then(level => setLevelNodes(level.nodes || {}))
                    .catch(err => console.error('Failed to refresh level nodes', err));
                onEventsMutated === null || onEventsMutated === void 0 ? void 0 : onEventsMutated();
                if (selectedEventId === eventId) {
                    setSelectedEventId(null);
                    setCurrentEvent(null);
                    setIsEditing(false);
                }
                // Notify other components that the event was deleted
                EventBus.emit('eventDeleted', eventId);
            }
            catch (err) {
                console.error('Error deleting event:', err);
                setError(`Failed to delete event: ${err.message}`);
            }
            finally {
                setIsLoading(false);
            }
        }
    });
    /**
     * Attempt to delete an event while checking for links. If links are found, show
     * a detailed warning in a modal. Otherwise delete right away.
     */
    // Bring an archived event back into the active set. It returns UNATTACHED —
    // deleting detached it from every cell — so the author re-places it on the map.
    const handleRestoreEvent = (eventId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        setRestoringId(eventId);
        setError(null);
        try {
            const restored = yield restoreEvent(levelId, eventId);
            setArchivedEvents(draft => draft.filter(e => e.id !== eventId));
            setEvents(draft => { draft.push(restored); });
            onEventsMutated === null || onEventsMutated === void 0 ? void 0 : onEventsMutated();
        }
        catch (err) {
            setError(`Failed to restore event: ${(_c = (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : err.message}`);
        }
        finally {
            setRestoringId(null);
        }
    });
    const attemptDeleteEvent = (eventId) => {
        const eventToDelete = events.find(e => e.id === eventId);
        if (!eventToDelete) {
            return;
        }
        // --- Search for links in scenarios ---
        const linkedScenarioNames = scenarios
            .filter(sc => { var _a; return (_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId) === eventId; }); })
            .map(sc => sc.name);
        // --- Search for links in map nodes via Random Encounters ---
        const directEncounterCoords = [];
        const allEncounterCoords = [];
        Object.entries(levelNodes).forEach(([coord, node]) => {
            var _a;
            const re = node.randomEncounters;
            if (!re || !re.enabled)
                return;
            if (re.encounterSelection === 'custom' && ((_a = re.selectedEncounters) === null || _a === void 0 ? void 0 : _a.includes(eventId))) {
                directEncounterCoords.push(coord);
            }
            else if (re.encounterSelection === 'all' && eventToDelete.isEncounter) {
                allEncounterCoords.push(coord);
            }
        });
        const hasLinks = linkedScenarioNames.length > 0 || directEncounterCoords.length > 0 || allEncounterCoords.length > 0;
        if (!hasLinks) {
            // No links — delete right away via the standard confirm
            handleDeleteEvent(eventId);
            return;
        }
        // Build a structured JSX warning message
        const warningMessage = (_jsxs("div", { className: "warning-message", children: [_jsx("div", { className: "warning-header", children: "Warning! This event is linked with the following entities:" }), _jsxs("div", { className: "connections-list", children: [linkedScenarioNames.length > 0 && (_jsxs("div", { className: "connection-group scenarios", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaLink, { className: "connection-icon" }), _jsx("span", { children: "Scenarios" })] }), _jsx("div", { className: "connection-items", children: linkedScenarioNames.map((name, index) => (_jsx("span", { className: "connection-item", children: name }, index))) })] })), directEncounterCoords.length > 0 && (_jsxs("div", { className: "connection-group direct-encounters", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaRandom, { className: "connection-icon" }), _jsx("span", { children: "Map nodes (custom encounters)" })] }), _jsx("div", { className: "connection-items", children: directEncounterCoords.map((coord, index) => (_jsx("span", { className: "connection-item", children: coord }, index))) })] })), allEncounterCoords.length > 0 && (_jsxs("div", { className: "connection-group all-encounters", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaRandom, { className: "connection-icon" }), _jsx("span", { children: "Map nodes (all encounters)" })] }), _jsx("div", { className: "connection-items", children: allEncounterCoords.map((coord, index) => (_jsx("span", { className: "connection-item", children: coord }, index))) })] }))] }), _jsx("div", { className: "warning-footer", children: "Deleting the event will remove these links. Do you want to continue?" })] }));
        setDeleteWarningMessage(warningMessage);
        setPendingDeleteEventId(eventId);
        setIsDeleteModalOpen(true);
    };
    // Handler for adding a trial option (ability)
    const handleAddTrialOption = () => {
        if (!currentEvent || currentEvent.encounters.length >= MAX_TRIAL_OPTIONS)
            return;
        // Find an ability that is not yet in the list
        const usedCharacteristics = currentEvent.encounters.map(opt => opt.ability);
        const availableCharacteristics = Object.values(AvatarAbility)
            .filter(char => !usedCharacteristics.includes(char));
        if (availableCharacteristics.length === 0)
            return;
        setCurrentEvent(draft => {
            if (!draft)
                return;
            draft.encounters.push({
                ability: availableCharacteristics[0],
                difficulty: DEFAULT_TRIAL_DIFFICULTY,
                powerCost: 10,
                failurePenalty: 5,
                rewardType: PrizeType.Knowledge,
                rewardAmount: 1,
                successText: "You successfully passed the obstacle!",
                failureText: "You failed the obstacle."
            });
        });
    };
    // Handler for deleting a trial option (ability)
    const handleDeleteTrialOption = (index) => {
        if (!currentEvent || currentEvent.encounters.length <= 1)
            return;
        setCurrentEvent(draft => {
            if (!draft)
                return;
            draft.encounters.splice(index, 1);
        });
    };
    // Handler for changing the ability in a trial option
    const handleChangeCharacteristic = (index, newCharacteristic) => {
        if (!currentEvent)
            return;
        // Check that this ability is not already selected in another option
        const isAlreadyUsed = currentEvent.encounters.some((opt, i) => i !== index && opt.ability === newCharacteristic);
        if (isAlreadyUsed)
            return;
        setCurrentEvent(draft => {
            if (!draft)
                return;
            draft.encounters[index].ability = newCharacteristic;
        });
    };
    // Handler for saving changes to the event
    const handleSaveEvent = () => {
        if (!currentEvent)
            return;
        // --- Validate numeric fields (as before) ---
        for (const opt of currentEvent.encounters) {
            if (typeof opt.difficulty !== 'number' || isNaN(opt.difficulty) ||
                typeof opt.powerCost !== 'number' || isNaN(opt.powerCost) ||
                typeof opt.failurePenalty !== 'number' || isNaN(opt.failurePenalty) ||
                typeof opt.rewardAmount !== 'number' || isNaN(opt.rewardAmount)) {
                setError('Please fill in all numeric fields for each challenge option.');
                return;
            }
        }
        // --- Determine the linked entities ---
        const linkedScenarioNames = scenarios
            .filter(sc => { var _a; return (_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId) === currentEvent.id; }); })
            .map(sc => sc.name);
        const directEncounterCoords = [];
        const allEncounterCoords = [];
        Object.entries(levelNodes).forEach(([coord, node]) => {
            var _a;
            const re = node.randomEncounters;
            if (!re || !re.enabled)
                return;
            if (re.encounterSelection === 'custom' && ((_a = re.selectedEncounters) === null || _a === void 0 ? void 0 : _a.includes(currentEvent.id))) {
                directEncounterCoords.push(coord);
            }
            else if (re.encounterSelection === 'all' && currentEvent.isEncounter) {
                allEncounterCoords.push(coord);
            }
        });
        const hasLinks = linkedScenarioNames.length > 0 || directEncounterCoords.length > 0 || allEncounterCoords.length > 0;
        const warnMsg = (_jsxs("div", { className: "warning-message", children: [_jsx("div", { className: "warning-header", children: hasLinks ? 'Saving this event will update the following entities:' : 'Save event?' }), hasLinks && (_jsxs("div", { className: "connections-list", children: [linkedScenarioNames.length > 0 && (_jsxs("div", { className: "connection-group scenarios", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaLink, { className: "connection-icon" }), " ", _jsx("span", { children: "Scenarios" })] }), _jsx("div", { className: "connection-items", children: linkedScenarioNames.map((name, idx) => _jsx("span", { className: "connection-item", children: name }, idx)) })] })), (directEncounterCoords.length > 0 || allEncounterCoords.length > 0) && (_jsxs("div", { className: "connection-group map-nodes", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaMap, { className: "connection-icon" }), " ", _jsx("span", { children: "Map nodes" })] }), _jsx("div", { className: "connection-items", children: [...directEncounterCoords, ...allEncounterCoords].map((coord, idx) => _jsx("span", { className: "connection-item", children: coord }, idx)) })] }))] })), _jsx("div", { className: "warning-footer", children: "Do you want to proceed and save changes?" })] }));
        setSaveWarningMessage(warnMsg);
        setPendingEventToSave(currentEvent);
        setIsSaveModalOpen(true);
    };
    const saveEvent = (eventToSave) => __awaiter(void 0, void 0, void 0, function* () {
        setIsSaving(true);
        setError(null);
        try {
            let savedEvent;
            if (selectedEventId) {
                // Update the existing event
                savedEvent = yield updateEvent(levelId, selectedEventId, eventToSave);
                yield syncScenarios(levelId, { event: savedEvent });
            }
            else {
                // Create a new event
                savedEvent = yield createEvent(levelId, eventToSave);
            }
            // Update the events list
            setEvents(draft => {
                const index = draft.findIndex(e => e.id === savedEvent.id);
                if (index >= 0) {
                    draft[index] = savedEvent;
                }
                else {
                    draft.push(savedEvent);
                }
            });
            // Notify other components
            EventBus.emit('eventUpdated', savedEvent);
            // Close the editor
            setIsEditing(false);
            setSelectedEventId(null);
            setCurrentEvent(null);
        }
        catch (err) {
            console.error('Error saving event:', err);
            setError(`Failed to save event: ${err.message}`);
        }
        finally {
            setIsSaving(false);
        }
    });
    const discardEditing = () => {
        setIsEditing(false);
        setCurrentEvent(null);
        setError(null);
    };
    const handleCancelEdit = () => {
        discardEditing();
    };
    // Notify the parent about unsaved changes
    useEffect(() => {
        if (onUnsavedChangesChange) {
            onUnsavedChangesChange(hasUnsavedChanges, (currentEvent === null || currentEvent === void 0 ? void 0 : currentEvent.title) || '');
        }
    }, [hasUnsavedChanges, currentEvent === null || currentEvent === void 0 ? void 0 : currentEvent.title]);
    // Register the save function
    useEffect(() => {
        if (onRegisterSave) {
            onRegisterSave(handleSaveEvent);
        }
    }, [onRegisterSave, handleSaveEvent]);
    // Handlers for event field changes
    const handleEventFieldChange = (field, value) => {
        setCurrentEvent(draft => {
            if (!draft)
                return;
            draft[field] = value;
        });
    };
    // Handlers for trial option field changes
    const handleTrialOptionFieldChange = (optionIndex, field, value) => {
        setCurrentEvent(draft => {
            if (!draft)
                return;
            draft.encounters[optionIndex][field] = value;
        });
    };
    // Handler for clearing filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedCharacteristics([]);
        setConnectionFilter('all');
        setTypeFilter('all');
    };
    // Handler for toggling ability selection
    const handleCharacteristicToggle = (characteristic) => {
        setSelectedCharacteristics(prev => {
            if (prev.includes(characteristic)) {
                return prev.filter(c => c !== characteristic);
            }
            else {
                return [...prev, characteristic];
            }
        });
    };
    // Handlers for the new filters
    const handleConnectionFilterToggle = (filter) => {
        setConnectionFilter(prev => prev === filter ? 'all' : filter);
    };
    const handleTypeFilterToggle = (filter) => {
        setTypeFilter(prev => prev === filter ? 'all' : filter);
    };
    // Handler for CSV import
    const handleImportCSV = () => {
        var _a;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    // Handler for file selection
    const handleFileSelect = (e) => __awaiter(void 0, void 0, void 0, function* () {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                alert('Please select a CSV file');
                return;
            }
            try {
                const content = yield file.text();
                const lines = content.split('\n');
                const headers = lines[0].split(',');
                // Simple CSV parsing example; for production use a more robust library
                const newEvents = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim())
                        continue;
                    const values = lines[i].split(',');
                    const event = createDefaultEvent(levelId);
                    // Basic fields
                    event.title = values[headers.indexOf('title')] || event.title;
                    event.description = values[headers.indexOf('description')] || event.description;
                    event.isEncounter = (values[headers.indexOf('isEncounter')] || '').toLowerCase() === 'true';
                    // ... more complex parsing logic for encounters and other fields could go here
                    newEvents.push(event);
                }
                // Save the new events to the database
                if (newEvents.length > 0) {
                    setIsLoading(true);
                    try {
                        for (const event of newEvents) {
                            yield createEvent(levelId, event);
                        }
                        // Reload the events
                        const eventsData = yield getLevelEvents(levelId);
                        setEvents(eventsData);
                        alert(`Successfully imported ${newEvents.length} events`);
                    }
                    catch (err) {
                        console.error('Error importing events:', err);
                        setError(`Failed to import events: ${err.message}`);
                    }
                    finally {
                        setIsLoading(false);
                    }
                }
            }
            catch (err) {
                console.error('Error reading CSV file:', err);
                alert(`Error reading CSV file: ${err.message}`);
            }
            // Reset the input value so the same file can be selected again
            e.target.value = '';
        }
    });
    // Handler for CSV export
    const handleExportCSV = () => {
        if (events.length === 0) {
            alert('No events to export');
            return;
        }
        const headers = [
            'id',
            'title',
            'description',
            'isEncounter',
            'trialOption1_characteristic',
            'trialOption1_difficulty',
            'trialOption2_characteristic',
            'trialOption2_difficulty',
            // ... add other fields as needed
        ].join(',');
        const rows = events.map(event => {
            var _a, _b, _c, _d;
            const row = [
                event.id,
                `"${event.title.replace(/"/g, '""')}"`,
                `"${event.description.replace(/"/g, '""')}"`,
                event.isEncounter,
                ((_a = event.encounters[0]) === null || _a === void 0 ? void 0 : _a.ability) || '',
                ((_b = event.encounters[0]) === null || _b === void 0 ? void 0 : _b.difficulty) || '',
                ((_c = event.encounters[1]) === null || _c === void 0 ? void 0 : _c.ability) || '',
                ((_d = event.encounters[1]) === null || _d === void 0 ? void 0 : _d.difficulty) || '',
                // ... add other fields as needed
            ];
            return row.join(',');
        });
        const csvContent = [headers, ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `level_${levelId}_events.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // Render the events list
    const renderEventsList = () => (_jsxs("div", { className: "events-list", children: [_jsx("div", { className: "events-toolbar", children: _jsx("div", { className: "width-container", children: _jsxs("div", { className: "toolbar-content", children: [_jsxs("div", { className: "search-container", children: [_jsx(FaSearch, { className: "search-icon", size: 14 }), _jsx("input", { type: "text", placeholder: "Search by title...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), className: "search-input" }), searchTerm && (_jsx("button", { className: "clear-search-button", onClick: () => setSearchTerm(''), title: "Clear search", children: _jsx(FaTimes, { size: 12 }) }))] }), _jsxs("div", { className: "filters-actions-row", children: [_jsxs("div", { className: "filters-container", children: [_jsx("div", { className: "characteristics-filter", children: _jsx("div", { className: "characteristic-filters", children: Object.values(AvatarAbility).map(characteristic => (_jsx("button", { className: `characteristic-filter-button ${selectedCharacteristics.includes(characteristic) ? 'active' : ''}`, style: {
                                                            backgroundColor: selectedCharacteristics.includes(characteristic)
                                                                ? ABILITY_COLORS[characteristic]
                                                                : 'transparent',
                                                            color: selectedCharacteristics.includes(characteristic)
                                                                ? 'white'
                                                                : ABILITY_COLORS[characteristic],
                                                            borderColor: ABILITY_BORDER_COLORS[characteristic]
                                                        }, onClick: () => handleCharacteristicToggle(characteristic), title: `Filter by ${characteristic}`, children: characteristic }, characteristic))) }) }), _jsxs("div", { className: "connection-filter", children: [_jsxs("button", { className: `connection-filter-button ${connectionFilter === 'connected' ? 'active' : ''}`, onClick: () => handleConnectionFilterToggle('connected'), title: "Show only events with scenario connections", children: [_jsx(FaLink, { size: 10 }), " Connected"] }), _jsx("button", { className: `connection-filter-button ${connectionFilter === 'unconnected' ? 'active' : ''}`, onClick: () => handleConnectionFilterToggle('unconnected'), title: "Show only events without scenario connections", children: "Unconnected" })] }), _jsxs("div", { className: "type-filter", children: [_jsx("button", { className: `type-filter-button challenge-button ${typeFilter === 'challenge' ? 'active' : ''}`, onClick: () => handleTypeFilterToggle('challenge'), title: "Show only challenge events", children: "Challenge" }), _jsxs("button", { className: `type-filter-button encounter-button ${typeFilter === 'encounter' ? 'active' : ''}`, onClick: () => handleTypeFilterToggle('encounter'), title: "Show only random encounter events", children: [_jsx(FaRandom, { size: 10 }), " Random Encounter"] })] }), (searchTerm || selectedCharacteristics.length > 0 || connectionFilter !== 'all' || typeFilter !== 'all') && (_jsxs("button", { className: "clear-filters-button", onClick: handleClearFilters, title: "Clear all filters", children: [_jsx(FaTimes, { size: 10 }), " Clear"] }))] }), _jsxs("div", { className: "import-export-container", children: [!readOnly && (_jsxs("button", { className: "import-button", onClick: handleImportCSV, title: "Import events from CSV", disabled: isLoading, children: [_jsx(FaFileImport, { size: 11 }), " Import"] })), _jsx("input", { type: "file", ref: fileInputRef, style: { display: 'none' }, accept: ".csv", onChange: handleFileSelect }), _jsxs("button", { className: "export-button", onClick: handleExportCSV, title: "Export events to CSV", disabled: isLoading, children: [_jsx(FaDownload, { size: 11 }), " Export"] })] })] })] }) }) }), _jsxs("div", { className: "events-content", children: [_jsxs("div", { className: "events-header", children: [_jsx("h2", { className: "section-title", children: "Events" }), !readOnly && (_jsxs("button", { className: "add-button", onClick: handleCreateEvent, title: "Create new event", disabled: isLoading, children: [_jsx(FaPlus, {}), " Add Event"] }))] }), error && _jsx("div", { className: "error-message", children: error }), isLoading ? (_jsx("div", { className: "loading-state", children: "Loading events..." })) : filteredEvents.length === 0 ? (_jsx("div", { className: "empty-state", children: events.length === 0 ? (_jsxs(_Fragment, { children: [_jsxs("p", { children: ["No events yet.", readOnly ? '' : ' Create your first event!'] }), !readOnly && (_jsxs("button", { onClick: handleCreateEvent, className: "primary-button", children: [_jsx(FaPlus, {}), " Create Event"] }))] })) : (_jsx("p", { children: "No events match your search criteria." })) })) : (_jsx("div", { className: "events-grid", children: filteredEvents.map(event => {
                            // Determine the linked scenarios
                            const linkedScenarios = scenarios.filter(sc => { var _a; return (_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId) === event.id; }); });
                            return (_jsxs("div", { className: `event-card ${selectedEventId === event.id ? 'selected' : ''}${readOnly ? ' read-only' : ''}`, onClick: readOnly ? undefined : () => handleEditEvent(event.id), children: [_jsx("div", { className: "event-card-background", style: (() => {
                                            if (event.backgroundImage === '___NODE___' || !event.backgroundImage) {
                                                return {};
                                            }
                                            return {
                                                backgroundImage: `url(${normalizeImageUrl(event.backgroundImage)})`,
                                            };
                                        })(), children: (event.backgroundImage === '___NODE___' || !event.backgroundImage) && (_jsx("div", { className: "event-card-node-icon", children: _jsx(FaImage, {}) })) }), _jsx("div", { className: "event-card-character", style: {
                                            backgroundImage: event.cardImage ? `url(${normalizeImageUrl(event.cardImage)})` : undefined,
                                        }, children: !event.cardImage && (_jsx("div", { className: "event-card-placeholder", children: _jsx(FaImage, {}) })) }), _jsx("div", { className: "event-card-overlay", children: _jsxs("div", { className: "event-card-content", children: [_jsx("h3", { className: "event-title", children: event.title }), _jsx("div", { className: "event-type-badge-container", children: _jsx("span", { className: `event-type-badge ${event.isEncounter ? 'encounter' : 'trial'}`, children: event.isEncounter ? 'Random Encounter' : 'Challenge' }) }), _jsx("p", { className: "event-description", children: event.description }), linkedScenarios.length > 0 && (() => {
                                                    const firstName = linkedScenarios[0].name;
                                                    const extra = linkedScenarios.length - 1;
                                                    const prefix = linkedScenarios.length === 1 ? 'Scenario' : 'Scenarios';
                                                    const mainLabel = `${prefix}: ${firstName}`;
                                                    return (_jsxs("div", { className: "link-badge-container", children: [_jsxs("span", { className: "link-badge", "data-tooltip": mainLabel, children: [_jsx(FaLink, { className: "link-icon" }), _jsx("span", { className: "link-text", children: mainLabel })] }), extra > 0 && (() => {
                                                                const tooltip = linkedScenarios.slice(1).map(sc => sc.name).join(', ');
                                                                return (_jsxs("span", { className: "link-badge extra-count", "data-tooltip": tooltip, children: ["+", extra] }));
                                                            })()] }));
                                                })(), _jsx("div", { className: "trial-options-preview", children: event.encounters.map((option, index) => (_jsxs("span", { className: "characteristic-tag", style: { backgroundColor: ABILITY_COLORS[option.ability] }, children: [option.ability, ": ", option.difficulty] }, index))) })] }) }), !readOnly && (_jsxs("div", { className: "event-card-actions", children: [_jsx("button", { className: "icon-button edit-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleEditEvent(event.id);
                                                }, title: "Edit", children: _jsx(FaPencilAlt, { size: 12 }) }), _jsx("button", { className: "icon-button delete-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    attemptDeleteEvent(event.id);
                                                }, disabled: isLoading, title: "Delete", children: _jsx(FaTrash, { size: 12 }) })] }))] }, event.id));
                        }) })), archivedEvents.length > 0 && (_jsxs("div", { className: "events-archive", children: [_jsxs("h3", { className: "archive-title", children: ["Archive (", archivedEvents.length, ")"] }), _jsx("div", { className: "archive-list", children: archivedEvents.map(ev => (_jsxs("div", { className: "archive-row", children: [_jsx("span", { className: "archive-name", children: ev.title }), !readOnly && (_jsx("button", { className: "restore-button", onClick: () => handleRestoreEvent(ev.id), disabled: restoringId === ev.id, children: restoringId === ev.id ? 'Restoring…' : 'Restore' }))] }, ev.id))) })] }))] })] }));
    // Render the event editing form
    const renderEventEditor = () => {
        if (!currentEvent)
            return null;
        return (_jsxs("div", { className: "event-editor", children: [_jsxs("div", { className: "editor-header", children: [_jsx("h2", { children: selectedEventId ? 'Edit Event' : 'Create New Event' }), _jsxs("div", { className: "editor-actions", children: [_jsxs("button", { className: "cancel-button", onClick: handleCancelEdit, disabled: isSaving, children: [_jsx(FaTimes, {}), " Cancel"] }), _jsx("button", { className: "save-button", onClick: handleSaveEvent, disabled: isSaving, children: isSaving ? 'Saving...' : 'Save Event' })] })] }), error && _jsx("div", { className: "error-message", children: error }), _jsxs("div", { className: "editor-form", children: [_jsxs("div", { className: "form-section", children: [_jsx("h3", { className: "section-title", children: "Basic Information" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "eventTitle", children: "Event Title:" }), _jsx("input", { type: "text", id: "eventTitle", value: currentEvent.title, onChange: (e) => handleEventFieldChange('title', e.target.value), placeholder: "Enter event title", disabled: isSaving })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "eventDescription", children: "Description:" }), _jsx("textarea", { id: "eventDescription", value: currentEvent.description, onChange: (e) => handleEventFieldChange('description', e.target.value), placeholder: "Enter event description", rows: 4, disabled: isSaving })] })] }), _jsxs("div", { className: "form-section", children: [_jsx("h3", { className: "section-title", children: "Images" }), _jsxs("div", { className: "image-upload-group", children: [_jsxs("div", { className: "background-selector-container", children: [_jsx("label", { children: "Obstacle scene (full-screen):" }), _jsxs("div", { className: "toggle-group", children: [_jsx("button", { type: "button", className: `toggle-button ${currentEvent.backgroundImage === '___NODE___' || (!currentEvent.backgroundImage) ? 'active' : ''}`, onClick: () => handleEventFieldChange('backgroundImage', '___NODE___'), children: "Node" }), _jsx("button", { type: "button", className: `toggle-button ${currentEvent.backgroundImage && currentEvent.backgroundImage !== '___NODE___' ? 'active' : ''}`, onClick: () => {
                                                                if (currentEvent.backgroundImage && currentEvent.backgroundImage !== '___NODE___') {
                                                                    // If an image is already selected, open the modal
                                                                    setShowBackgroundModal(true);
                                                                }
                                                                else {
                                                                    // If switching to Custom, open the modal
                                                                    setShowBackgroundModal(true);
                                                                }
                                                            }, children: "Custom" })] }), _jsx("div", { className: "background-preview-container", children: (() => {
                                                        // Show the node icon for Node mode or when backgroundImage is not set
                                                        if (currentEvent.backgroundImage === '___NODE___' || !currentEvent.backgroundImage) {
                                                            return (_jsx("div", { className: "full-background-preview icon-mode node-mode", children: _jsx("div", { className: "large-icon", children: _jsx(FaImage, {}) }) }));
                                                        }
                                                        // Show the image for Custom mode
                                                        if (currentEvent.backgroundImage && currentEvent.backgroundImage !== '___NODE___') {
                                                            const displayImage = normalizeImageUrl(currentEvent.backgroundImage);
                                                            if (displayImage) {
                                                                return (_jsxs("div", { className: "full-background-preview custom-mode", onMouseMove: (e) => {
                                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                                        const x = e.clientX;
                                                                        const y = e.clientY;
                                                                        const windowHeight = window.innerHeight;
                                                                        const previewHeight = 300;
                                                                        // Compute the distance from the cursor to the element center
                                                                        const centerX = rect.left + rect.width / 2;
                                                                        const centerY = rect.top + rect.height / 2;
                                                                        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                                                                        // Position the preview next to the cursor
                                                                        e.currentTarget.style.setProperty('--preview-x', `${x + 15}px`);
                                                                        // Check whether the preview would overflow the bottom edge of the screen
                                                                        if (y + previewHeight > windowHeight) {
                                                                            e.currentTarget.style.setProperty('--preview-y', `${y - previewHeight - 20}px`);
                                                                        }
                                                                        else {
                                                                            e.currentTarget.style.setProperty('--preview-y', `${y - 10}px`);
                                                                        }
                                                                        // Show the preview when approaching the element
                                                                        if (distance <= 80) {
                                                                            e.currentTarget.style.setProperty('--preview-visible', '1');
                                                                        }
                                                                        else {
                                                                            e.currentTarget.style.setProperty('--preview-visible', '0');
                                                                        }
                                                                    }, onMouseLeave: (e) => {
                                                                        e.currentTarget.style.setProperty('--preview-visible', '0');
                                                                    }, children: [_jsx("img", { src: displayImage, alt: "Background preview", onClick: () => setShowBackgroundModal(true) }), _jsx("img", { src: displayImage, alt: "Background preview", className: "background-preview-popup" })] }));
                                                            }
                                                        }
                                                        return (_jsx("div", { className: "full-background-preview custom-mode placeholder", children: _jsxs("div", { className: "placeholder-text", children: [_jsx("span", { children: "No image selected" }), _jsx("button", { type: "button", onClick: () => setShowBackgroundModal(true), children: "Select Image" })] }) }));
                                                    })() })] }), _jsxs("div", { className: "card-image-selector-container", children: [_jsx("label", { children: "Obstacle card (also on the cell):" }), _jsx("div", { className: "toggle-group", children: _jsx("button", { type: "button", className: "toggle-button active", onClick: () => {
                                                            // Toggle logic could go here in the future
                                                            // For now always Custom mode
                                                            setShowCardModal(true);
                                                        }, children: "Custom" }) }), _jsx("div", { className: "card-preview-container", children: (() => {
                                                        // Show the image if one is selected
                                                        if (currentEvent.cardImage) {
                                                            const displayImage = normalizeImageUrl(currentEvent.cardImage);
                                                            if (displayImage) {
                                                                return (_jsxs("div", { className: "full-card-preview custom-mode", onMouseMove: (e) => {
                                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                                        const x = e.clientX;
                                                                        const y = e.clientY;
                                                                        const windowHeight = window.innerHeight;
                                                                        const previewHeight = 300;
                                                                        // Compute the distance from the cursor to the element center
                                                                        const centerX = rect.left + rect.width / 2;
                                                                        const centerY = rect.top + rect.height / 2;
                                                                        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                                                                        // Position the preview next to the cursor
                                                                        e.currentTarget.style.setProperty('--preview-x', `${x + 15}px`);
                                                                        // Check whether the preview would overflow the bottom edge of the screen
                                                                        if (y + previewHeight > windowHeight) {
                                                                            e.currentTarget.style.setProperty('--preview-y', `${y - previewHeight - 20}px`);
                                                                        }
                                                                        else {
                                                                            e.currentTarget.style.setProperty('--preview-y', `${y - 10}px`);
                                                                        }
                                                                        // Show the preview when approaching the element
                                                                        if (distance <= 80) {
                                                                            e.currentTarget.style.setProperty('--preview-visible', '1');
                                                                        }
                                                                        else {
                                                                            e.currentTarget.style.setProperty('--preview-visible', '0');
                                                                        }
                                                                    }, onMouseLeave: (e) => {
                                                                        e.currentTarget.style.setProperty('--preview-visible', '0');
                                                                    }, children: [_jsx("img", { src: displayImage, alt: "Card preview", onClick: () => setShowCardModal(true) }), _jsx("img", { src: displayImage, alt: "Card preview", className: "card-preview-popup" })] }));
                                                            }
                                                        }
                                                        // Show a placeholder if no image is selected
                                                        return (_jsx("div", { className: "full-card-preview custom-mode placeholder", children: _jsxs("div", { className: "placeholder-text", children: [_jsx("span", { children: "No image selected" }), _jsx("button", { type: "button", onClick: () => setShowCardModal(true), children: "Select Image" })] }) }));
                                                    })() })] })] })] }), _jsxs("div", { className: "form-section", children: [_jsxs("div", { className: "section-header", children: [_jsx("h3", { className: "section-title", children: "Challenge Options" }), currentEvent.encounters.length < MAX_TRIAL_OPTIONS && (_jsxs("button", { className: "add-option-button", onClick: handleAddTrialOption, title: "Add another characteristic", disabled: isSaving, children: [_jsx(FaPlus, {}), " Add Option"] }))] }), _jsx("div", { className: "trial-options-container", children: currentEvent.encounters.map((option, index) => (_jsxs("div", { className: "trial-option-card", children: [_jsxs("div", { className: "trial-option-header", children: [_jsx(IconToken, { color: ABILITY_COLORS[option.ability], title: option.ability, children: _jsx("img", { src: ABILITY_ICON[option.ability], alt: option.ability }) }), _jsx("select", { value: option.ability, onChange: (e) => handleChangeCharacteristic(index, e.target.value), className: "characteristic-select", disabled: isSaving, children: Object.values(AvatarAbility).map(char => (_jsx("option", { value: char, disabled: char !== option.ability &&
                                                                currentEvent.encounters.some(opt => opt.ability === char), children: char }, char))) }), currentEvent.encounters.length > 1 && (_jsx("button", { className: "delete-option-button", onClick: () => handleDeleteTrialOption(index), disabled: isSaving, children: _jsx(FaTrash, {}) }))] }), _jsxs("div", { className: "trial-option-parameters", children: [_jsxs("div", { className: "parameter-group", children: [_jsx("label", { children: "Difficulty:" }), _jsx("input", { type: "number", min: "1", max: "20", value: option.difficulty !== undefined ? option.difficulty : '', onChange: (e) => {
                                                                    const val = e.target.value;
                                                                    handleTrialOptionFieldChange(index, 'difficulty', val === '' ? val : parseInt(val, 10));
                                                                }, disabled: isSaving })] }), _jsxs("div", { className: "parameter-group", children: [_jsx("label", { children: "Power Cost:" }), _jsx("input", { type: "number", min: "0", value: option.powerCost !== undefined ? option.powerCost : '', onChange: (e) => {
                                                                    const val = e.target.value;
                                                                    handleTrialOptionFieldChange(index, 'powerCost', val === '' ? val : parseInt(val, 10));
                                                                }, disabled: isSaving })] })] }), _jsx("div", { className: "trial-option-reward", children: _jsxs("div", { className: "parameter-group", children: [_jsx("label", { children: "Reward Amount:" }), _jsx("input", { type: "number", min: "0", value: option.rewardAmount !== undefined ? option.rewardAmount : '', onChange: (e) => {
                                                                const val = e.target.value;
                                                                handleTrialOptionFieldChange(index, 'rewardAmount', val === '' ? val : parseInt(val, 10));
                                                                // Also set the reward type to Data
                                                                handleTrialOptionFieldChange(index, 'rewardType', PrizeType.Knowledge);
                                                            }, disabled: isSaving })] }) }), _jsxs("div", { className: "outcome-texts", children: [_jsxs("div", { className: "outcome-group success", children: [_jsx("label", { children: "Success Text:" }), _jsx("textarea", { value: option.successText, onChange: (e) => handleTrialOptionFieldChange(index, 'successText', e.target.value), rows: 3, placeholder: "Text shown on successful challenge completion", disabled: isSaving })] }), _jsxs("div", { className: "outcome-group failure", children: [_jsx("label", { children: "Failure Text:" }), _jsx("textarea", { value: option.failureText, onChange: (e) => handleTrialOptionFieldChange(index, 'failureText', e.target.value), rows: 3, placeholder: "Text shown on failed challenge", disabled: isSaving })] })] })] }, index))) })] })] })] }));
    };
    return (_jsxs("div", { className: "event-editor-container", children: [isEditing ? renderEventEditor() : renderEventsList(), _jsx(ConfirmationModal, { isOpen: isDeleteModalOpen, onClose: () => setIsDeleteModalOpen(false), onConfirm: () => {
                    if (pendingDeleteEventId) {
                        handleDeleteEvent(pendingDeleteEventId, true);
                    }
                    setIsDeleteModalOpen(false);
                }, title: "Delete Event", message: deleteWarningMessage, confirmText: "Delete", cancelText: "Cancel", isLoading: isLoading }), _jsx(ConfirmationModal, { isOpen: isSaveModalOpen, onClose: () => { setIsSaveModalOpen(false); setPendingEventToSave(null); }, onConfirm: () => {
                    if (pendingEventToSave) {
                        saveEvent(pendingEventToSave);
                    }
                    setIsSaveModalOpen(false);
                    setPendingEventToSave(null);
                }, title: "Save Event", message: saveWarningMessage, confirmText: "Save", cancelText: "Cancel", isLoading: isSaving }), _jsx(ImageSelectModal, { isOpen: showBackgroundModal, onClose: () => setShowBackgroundModal(false), onSelectImage: (imageUrl) => handleEventFieldChange('backgroundImage', imageUrl), images: eventImages, title: "Select the obstacle scene", isImageUsed: isEventImageUsed }), _jsx(ImageSelectModal, { isOpen: showCardModal, onClose: () => setShowCardModal(false), onSelectImage: (imageUrl) => handleEventFieldChange('cardImage', imageUrl), images: eventImages, title: "Select the obstacle card", isImageUsed: isEventImageUsed })] }));
};
function syncScenarios(levelId, eventOrId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const hasEvent = eventOrId.event != null;
        const hasEventId = eventOrId.eventId != null;
        // Exactly one input must be provided - either the whole event or its id.
        if (hasEvent == hasEventId)
            throw new Error();
        let eventId;
        if (hasEvent) {
            if (((_a = eventOrId.event) === null || _a === void 0 ? void 0 : _a.levelId) !== levelId)
                throw new Error();
            eventId = eventOrId.event.id;
        }
        else {
            if (eventOrId.eventId == null)
                throw new Error();
            eventId = eventOrId.eventId;
        }
        const scenarios = yield getLevelScenarios(levelId);
        const affectedScenarios = scenarios.filter(scenario => Array.isArray(scenario.nodes) &&
            scenario.nodes.some(node => isEventNode(node, eventId)));
        if (!affectedScenarios.length)
            return;
        for (const affectedScenario of affectedScenarios)
            yield syncScenario(eventOrId, affectedScenario);
    });
}
function syncScenario(eventOrId, scenario) {
    return __awaiter(this, void 0, void 0, function* () {
        const event = eventOrId.event;
        if (event != null) {
            const eventNodes = scenario.nodes.filter(x => isEventNode(x, event.id));
            if (!eventNodes.length)
                return;
            if (eventNodes.length > 1)
                throw new Error();
            const eventData = eventNodes[0].data;
            if (eventData == null || eventData.type !== 'event')
                throw new Error();
            eventData.text = event.title;
        }
        else {
            const eventId = eventOrId.eventId;
            if (eventId == null)
                throw new Error();
            if (!Array.isArray(scenario.nodes) || !scenario.nodes.length)
                return;
            scenario.nodes = scenario.nodes.filter(x => !isEventNode(x, eventId));
        }
        yield updateScenario(scenario.levelId, scenario.id, scenario);
    });
}
function isEventNode(node, eventId) {
    // The node type must be 'event'.
    if (node.type != 'event')
        return false;
    const data = node.data;
    // The type stored in the data field (!) must also be 'event'. This is not the same as what we checked above.
    if (data == null || data.type != 'event')
        return false;
    // The 'data' object has both an id field and an eventId field - it is important not to confuse them here.
    return data.eventId === eventId;
}
